import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { visitanteService } from '../services';
import { Search, Package, Eye, AlertCircle } from 'lucide-react';

const AreaVisitantes = () => {
  const { user } = useAuth();
  const [medicamentos, setMedicamentos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });

  const carregarMedicamentos = async (page = 1, searchTerm = '') => {
    try {
      setLoading(true);
      const response = await visitanteService.getMedicamentos({
        page,
        limit: 10,
        search: searchTerm
      });
      
      setMedicamentos(response.medicamentos || []);
      setPagination(response.pagination || {});
    } catch (error) {
      console.error('Erro ao carregar medicamentos:', error);
      setMedicamentos([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarMedicamentos();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    carregarMedicamentos(1, search);
  };

  const handlePageChange = (newPage) => {
    carregarMedicamentos(newPage, search);
  };

  const formatarDataValidade = (dataValidade) => {
    const data = new Date(dataValidade);
    return data.toLocaleDateString('pt-BR');
  };

  const obterStatusEstoque = (estoque) => {
    if (estoque > 50) return { cor: '#28a745', texto: 'Em estoque' };
    if (estoque > 10) return { cor: '#ffc107', texto: 'Estoque baixo' };
    return { cor: '#dc3545', texto: 'Estoque crítico' };
  };

  return (
    <div className="container">
      {/* Header */}
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ color: '#333', margin: '0', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Eye size={32} />
          Área do Visitante
        </h1>
        <p style={{ color: '#667', margin: '5px 0 0 0' }}>
          Visualização dos medicamentos disponíveis em estoque
        </p>
        <small style={{ color: '#999', fontSize: '14px' }}>
          Bem-vindo, {user?.nome} - Acesso apenas para consulta
        </small>
      </div>

      {/* Barra de Pesquisa */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <form onSubmit={handleSearch}>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <Search 
                size={20} 
                style={{ 
                  position: 'absolute', 
                  left: '12px', 
                  top: '50%', 
                  transform: 'translateY(-50%)',
                  color: '#999'
                }} 
              />
              <input
                type="text"
                placeholder="Pesquisar medicamento por nome..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 12px 12px 45px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '16px'
                }}
              />
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
              style={{ padding: '12px 24px' }}
            >
              {loading ? 'Buscando...' : 'Pesquisar'}
            </button>
          </div>
        </form>
      </div>

      {/* Informações */}
      <div className="card" style={{ marginBottom: '20px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
          <AlertCircle size={20} style={{ color: '#17a2b8' }} />
          <strong style={{ color: '#17a2b8' }}>Informações Importantes</strong>
        </div>
        <ul style={{ margin: '0', paddingLeft: '20px', color: '#667' }}>
          <li>Esta área permite apenas visualização dos medicamentos disponíveis</li>
          <li>A pesquisa é feita exclusivamente pelo nome do medicamento</li>
          <li>Apenas medicamentos com estoque disponível são exibidos</li>
          <li>Para solicitar medicamentos, entre em contato com a farmácia</li>
        </ul>
      </div>

      {/* Tabela de Medicamentos */}
      <div className="card">
        <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: '0', color: '#333' }}>
            <Package size={24} style={{ marginRight: '10px' }} />
            Medicamentos Disponíveis
          </h3>
          <span style={{ color: '#667', fontSize: '14px' }}>
            {pagination.totalItems} medicamento(s) encontrado(s)
          </span>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div className="spinner" style={{ margin: '0 auto 20px auto' }}></div>
            <p>Carregando medicamentos...</p>
          </div>
        ) : medicamentos.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#667' }}>
            <Package size={48} style={{ marginBottom: '20px', opacity: 0.3 }} />
            <h4>Nenhum medicamento encontrado</h4>
            <p>Tente uma pesquisa diferente ou verifique se há medicamentos disponíveis.</p>
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>
                      Medicamento
                    </th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>
                      Princípio Ativo
                    </th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>
                      Categoria
                    </th>
                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>
                      Estoque
                    </th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>
                      Fabricante
                    </th>
                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>
                      Validade
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {medicamentos.map((item) => {
                    const medicamento = item.medicamentoRegistrado;
                    const statusEstoque = obterStatusEstoque(item.estoque);
                    
                    return (
                      <tr key={item.id} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '12px' }}>
                          <div>
                            <strong style={{ color: '#333' }}>{medicamento.nome}</strong>
                            {medicamento.dosagem && (
                              <div style={{ fontSize: '12px', color: '#667' }}>
                                {medicamento.dosagem} • {medicamento.formaFarmaceutica}
                              </div>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: '12px', color: '#667' }}>
                          {medicamento.principioAtivo}
                        </td>
                        <td style={{ padding: '12px' }}>
                          <span style={{
                            backgroundColor: '#e9ecef',
                            color: '#495057',
                            padding: '4px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '500'
                          }}>
                            {medicamento.categoria}
                          </span>
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <span style={{
                            color: statusEstoque.cor,
                            fontWeight: '600',
                            fontSize: '14px'
                          }}>
                            {item.estoque} un.
                          </span>
                          <div style={{ fontSize: '11px', color: statusEstoque.cor }}>
                            {statusEstoque.texto}
                          </div>
                        </td>
                        <td style={{ padding: '12px', color: '#667' }}>
                          {item.fabricante}
                          {item.lote && (
                            <div style={{ fontSize: '12px', color: '#999' }}>
                              Lote: {item.lote}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center', color: '#667' }}>
                          {formatarDataValidade(item.dataValidade)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Paginação */}
            {pagination.totalPages > 1 && (
              <div style={{ 
                marginTop: '20px', 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                gap: '10px'
              }}>
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={pagination.currentPage === 1}
                  className="btn btn-outline-secondary"
                  style={{ padding: '8px 16px' }}
                >
                  Anterior
                </button>
                
                <span style={{ color: '#667', fontSize: '14px' }}>
                  Página {pagination.currentPage} de {pagination.totalPages}
                </span>
                
                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={pagination.currentPage === pagination.totalPages}
                  className="btn btn-outline-secondary"
                  style={{ padding: '8px 16px' }}
                >
                  Próxima
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AreaVisitantes;